import * as React from "react";
import clsx from "clsx";
import type { HTMLAttributes, ReactNode } from "react";
import { text } from "./Text.style.css";
import type { TextLevel, TextColor, TextWeight, TextClamp } from "./Text.types";

export interface TextProps extends HTMLAttributes<HTMLElement> {
  level?: TextLevel;
  color?: TextColor;
  weight?: TextWeight;
  clamp?: TextClamp;
  children: ReactNode;
  as?: keyof JSX.IntrinsicElements;
}

// Map text levels to appropriate HTML elements
const getElementType = (level: TextLevel): keyof JSX.IntrinsicElements => {
  switch (level) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      return level;
    case "title-large":
    case "title-small":
      return "h5";
    case "body":
    case "body-large":
    case "body-medium":
    case "body-small":
      return "p";
    default:
      return "p";
  }
};

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  {
    level = "body-medium",
    color = "text",
    weight = "normal",
    clamp,
    children,
    as,
    className,
    ...rest
  },
  ref
) {
  const Component = as || getElementType(level as TextLevel);

  return React.createElement(
    Component,
    {
      ref,
      className: clsx(
        text({
          level: level as TextLevel,
          color: color as TextColor,
          weight: weight as TextWeight,
          clamp: clamp as 1 | 2 | 3 | 4 | 5 | undefined,
        }),
        className
      ),
      ...rest,
    },
    children
  );
});
